/**
 * GRIDHEIMR — Bot Info Component
 *
 * Displays detailed bot information including status, resources, and metadata.
 */

import React, { useState, useEffect } from "react";
import type { Bot } from "../api/types";
import { BotStatus, BotType } from "../api/types";
import { useWebSocket, WebSocketState, applyBotUpdate } from "../hooks/useWebSocket";
import { BLUE, YELLOW, GREEN, RED, DARK_BG, PIXEL, MONO } from "../theme";

/**
 * Component props
 */
interface BotInfoProps {
  bot: Bot;
}

/**
 * Get status display properties
 */
function getStatusDisplay(status: BotStatus): {
  icon: string;
  color: string;
  label: string;
} {
  switch (status) {
    case BotStatus.RUNNING:
      return { icon: "✅", color: GREEN, label: "Running" };
    case BotStatus.STOPPED:
      return { icon: "⏸️", color: "rgba(255,255,255,0.3)", label: "Stopped" };
    case BotStatus.STARTING:
      return { icon: "🔄", color: BLUE, label: "Starting..." };
    case BotStatus.STOPPING:
      return { icon: "🔄", color: YELLOW, label: "Stopping..." };
    case BotStatus.CREATING:
      return { icon: "🔄", color: BLUE, label: "Creating..." };
    case BotStatus.FAILED:
      return { icon: "❌", color: RED, label: "Error" };
    case BotStatus.DESTROYED:
      return { icon: "💀", color: "rgba(255,255,255,0.2)", label: "Destroyed" };
    default:
      return { icon: "❓", color: "rgba(255,255,255,0.3)", label: status };
  }
}

/**
 * Get bot type display icon
 */
function getBotTypeIcon(botType: BotType): string {
  switch (botType) {
    case BotType.MOLTBOT:
      return "\u{1F916}";
    case BotType.OPENCLAW:
      return "\u{1F99E}";
    case BotType.PICOCLAW:
      return "\u{1F980}";
    case BotType.PICOBOT:
      return "\u{1F41E}";
    case BotType.NANOBOT:
      return "\u{1F9EC}";
    case BotType.CUSTOM:
      return "\u{1F527}";
    default:
      return "\u{1F916}";
  }
}

/**
 * Format timestamp to readable date
 */
function formatDate(timestamp?: string): string {
  if (!timestamp) return "Never";

  const date = new Date(timestamp);
  return date.toLocaleString();
}

/**
 * Format timestamp to relative time
 */
function formatRelativeTime(timestamp?: string): string {
  if (!timestamp) return "Never";

  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

/**
 * Bot Info Component
 *
 * Displays detailed information about a bot instance with real-time WebSocket updates.
 */
export function BotInfo({ bot: initialBot }: BotInfoProps) {
  const [bot, setBot] = useState<Bot>(initialBot);
  const { connectionState, lastUpdate } = useWebSocket(bot.id, (update) => {
    setBot((prevBot) => applyBotUpdate(prevBot, update));
  });

  // Update local state when prop changes
  useEffect(() => {
    setBot(initialBot);
  }, [initialBot]);

  const statusDisplay = getStatusDisplay(bot.status);
  const botTypeIcon = getBotTypeIcon(bot.bot_type);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.typeIcon}>{botTypeIcon}</span>
          <div>
            <h1 style={styles.botName}>{bot.name}</h1>
            <p style={styles.botId}>ID: {bot.id}</p>
          </div>
        </div>
        <div style={{ ...styles.statusBadge, borderColor: statusDisplay.color, color: statusDisplay.color }}>
          <span style={styles.statusIcon}>{statusDisplay.icon}</span>
          <span style={styles.statusLabel}>{statusDisplay.label}</span>
        </div>
      </div>

      {/* Error Message */}
      {bot.error_message && (
        <div style={styles.errorBanner}>
          <span style={styles.errorIcon}>⚠️</span>
          <span style={styles.errorText}>{bot.error_message}</span>
        </div>
      )}

      {/* WebSocket Connection Indicator */}
      {connectionState === WebSocketState.CONNECTED && (
        <div style={styles.wsIndicator}>
          <span style={styles.wsIcon}>🟢</span>
          <span style={styles.wsText}>Real-time updates active</span>
        </div>
      )}

      {/* Info Sections */}
      <div style={styles.sectionsGrid}>
        {/* General Info */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>General Information</h3>
          <div style={styles.infoList}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Name</span>
              <span style={styles.infoValue}>{bot.name}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Type</span>
              <span style={styles.infoValue}>{bot.bot_type}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Status</span>
              <span style={styles.infoValue}>{statusDisplay.label}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Created</span>
              <span style={styles.infoValue}>{formatDate(bot.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Resources */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Resources</h3>
          <div style={styles.infoList}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>vCPUs</span>
              <span style={styles.infoValue}>{bot.resources.vcpus}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Memory</span>
              <span style={styles.infoValue}>{bot.resources.memory_mb} MB</span>
            </div>
          </div>
        </div>

        {/* Activity */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Activity</h3>
          <div style={styles.infoList}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Last Active</span>
              <span style={styles.infoValue}>{formatRelativeTime(bot.last_active_at)}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Started At</span>
              <span style={styles.infoValue}>{formatDate(bot.started_at)}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Stopped At</span>
              <span style={styles.infoValue}>{formatDate(bot.stopped_at)}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Updated</span>
              <span style={styles.infoValue}>{formatDate(bot.updated_at)}</span>
            </div>
          </div>
        </div>

        {/* Skills */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Skills ({bot.skills.length})</h3>
          {bot.skills.length > 0 ? (
            <div style={styles.skillsList}>
              {bot.skills.map((skill, index) => (
                <div key={index} style={styles.skillItem}>
                  <span style={styles.skillName}>🔌 {skill.name}</span>
                  {skill.version && (
                    <span style={styles.skillVersion}>v{skill.version}</span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={styles.emptyMessage}>No skills installed</p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Inline styles
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "1.5rem",
    paddingBottom: "1.5rem",
    borderBottom: `1px solid rgba(0,106,167,0.15)`,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
  },
  typeIcon: {
    fontSize: "3rem",
  },
  botName: {
    margin: 0,
    fontFamily: PIXEL,
    fontSize: "clamp(0.7rem, 2vw, 1rem)",
    fontWeight: 400,
    color: "#fff",
    letterSpacing: "0.06em",
    textShadow: `0 0 12px rgba(0,106,167,0.4)`,
  },
  botId: {
    margin: "0.5rem 0 0 0",
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.4)",
  },
  statusBadge: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "6px 12px",
    border: "1px solid",
    fontFamily: PIXEL,
    fontSize: "0.42rem",
    fontWeight: 400,
    letterSpacing: "0.08em",
  },
  statusIcon: {
    fontSize: "1rem",
  },
  statusLabel: {
    lineHeight: 1,
  },
  errorBanner: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    background: "rgba(239,68,68,0.1)",
    border: "1px solid rgba(239,68,68,0.3)",
    padding: "1rem",
    marginBottom: "1.5rem",
  },
  errorIcon: {
    fontSize: "1.25rem",
  },
  errorText: {
    flex: 1,
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "#fca5a5",
  },
  sectionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "1px",
    background: `rgba(0,106,167,0.06)`,
    border: `1px solid rgba(0,106,167,0.15)`,
  },
  section: {
    background: DARK_BG,
    padding: "1.5rem",
  },
  sectionTitle: {
    margin: "0 0 1rem 0",
    fontFamily: PIXEL,
    fontSize: "0.5rem",
    fontWeight: 400,
    color: YELLOW,
    letterSpacing: "0.1em",
    textShadow: `0 0 8px rgba(254,204,0,0.3)`,
  },
  infoList: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  infoItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "1rem",
  },
  infoLabel: {
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.45)",
  },
  infoValue: {
    fontFamily: MONO,
    fontSize: "0.85rem",
    fontWeight: 500,
    color: "rgba(255,255,255,0.8)",
    textAlign: "right",
  },
  skillsList: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  skillItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "1rem",
    padding: "0.5rem 0.75rem",
    background: "rgba(0,106,167,0.04)",
    border: `1px solid rgba(0,106,167,0.1)`,
  },
  skillName: {
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.8)",
  },
  skillVersion: {
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.4)",
  },
  emptyMessage: {
    textAlign: "center",
    fontFamily: MONO,
    color: "rgba(255,255,255,0.3)",
    fontSize: "0.8rem",
    margin: 0,
  },
  wsIndicator: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    background: `rgba(0,201,122,0.08)`,
    border: `1px solid rgba(0,201,122,0.25)`,
    padding: "0.75rem 1rem",
    marginBottom: "1.5rem",
  },
  wsIcon: {
    fontSize: "0.75rem",
  },
  wsText: {
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: GREEN,
  },
};
